import React, { useState, useEffect } from 'react';
import UdemyFilmVideos from "./UdemyFilmVideos";

const UdemyCourseStructure = ({ course, updateCourse }) => {
  const [sections, setSections] = useState(course?.sections || []);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newLectureTitle, setNewLectureTitle] = useState({}); // (se mantiene por compatibilidad aunque ya no se usa aquí)

  useEffect(() => {
    setSections(course?.sections || []);
  }, [course]);

  // Agregar sección (igual que tu lógica: actualiza el curso completo con updateCourse)
  const addSection = async () => {
    if (!newSectionTitle.trim()) return;

    try {
      const newSection = {
        id: Date.now(), // Temporal hasta respuesta del backend
        title: newSectionTitle,
        lectures: [],
        position: sections.length,
        expanded: true, // abrir al crear
      };

      const updatedCourse = { 
        ...course, 
        sections: [...(course.sections || []), newSection] 
      };

      await updateCourse(updatedCourse);
      setNewSectionTitle('');
    } catch (error) {
      console.error('Error detallado al agregar sección:', {
        error: error.message,
        course,
        newSectionTitle
      });
      alert('Error al agregar sección. Por favor, inténtalo de nuevo.');
    }
  };

  // Ya no agregamos la lecture “mock” desde aquí.
  // Ahora la subida real de video y la creación de la lecture la hace UdemyFilmVideos.
  const addLecture = (_sectionId) => {
    alert('La creación de clases ahora se realiza con el cargador de video en la sección (subida real a la nube).');
  };

  const deleteSection = (sectionId) => {
    const updatedSections = sections.filter(section => section.id !== sectionId);
    setSections(updatedSections);
    updateCourse({ ...course, sections: updatedSections });
  };

  const deleteLecture = (sectionId, lectureId) => {
    // Esta función manipulaba el estado local de lectures “mock”.
    // Si aún quieres poder borrar del estado local, lo conservamos,
    // pero para la BD real debes implementar un DELETE en el backend y llamarlo desde UdemyFilmVideos.
    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return { ...section, lectures: (section.lectures || []).filter(lecture => lecture.id !== lectureId) };
      }
      return section;
    });
    setSections(updatedSections);
    updateCourse({ ...course, sections: updatedSections });
  };

  const toggleSection = (id) => {
    setSections(sections.map(section =>
      section.id === id ? { ...section, expanded: !section.expanded } : section
    ));
  };

  if (!course) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-600">
        Selecciona un curso para gestionar su estructura.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold mb-4">Estructura del curso: {course.title}</h2>

      <div className="mb-4 flex">
        <input
          type="text"
          value={newSectionTitle}
          onChange={(e) => setNewSectionTitle(e.target.value)}
          placeholder="Título de la nueva sección"
          className="flex-1 px-4 py-2 border rounded-l focus:ring-2 focus:ring-purple-600"
        />
        <button
          onClick={addSection}
          className="px-4 py-2 bg-purple-600 text-white rounded-r hover:bg-purple-700"
        >
          Agregar Sección
        </button>
      </div>

      <div className="space-y-2">
        {sections.length === 0 && <p className="text-gray-600">No hay secciones en este curso. ¡Agrega una!</p>}
        {sections.map(section => (
          <div key={section.id} className="border border-gray-200 rounded">
            <div
              className="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection(section.id)}
            >
              <div className="font-medium">{section.title}</div>
              <div className="flex items-center">
                {/* Este contador se basa en la estructura local. 
                   Los videos reales se listan dentro de UdemyFilmVideos */}
                <span className="text-sm text-gray-500 mr-3">{section.lectures?.length || 0} clases</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }}
                  className="text-red-500 hover:text-red-700 ml-2"
                  title="Eliminar sección"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <svg
                  className={`w-5 h-5 text-gray-500 transform ${section.expanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {section.expanded && (
              <div className="p-3 border-t border-gray-200">
                {/* Antes: input + botón para “Agregar Clase” mock.
                    Ahora: Componente que sube videos REALES y lista desde la BD */}
                <UdemyFilmVideos
                  course={course}
                  updateCourse={updateCourse}
                  sectionId={section.id}
                  compact
                />

                {/* Si quieres mantener botones locales, los dejamos deshabilitados */}
                <div className="flex items-center mt-3 opacity-60 pointer-events-none">
                  <input
                    type="text"
                    value={newLectureTitle[section.id] || ''}
                    onChange={(e) => setNewLectureTitle(prev => ({ ...prev, [section.id]: e.target.value }))}
                    placeholder="(Deshabilitado) Título de la nueva clase local"
                    className="flex-1 px-3 py-1 border rounded"
                    disabled
                  />
                  <button
                    onClick={() => addLecture(section.id)}
                    className="ml-2 px-3 py-1 bg-gray-200 rounded"
                    disabled
                  >
                    Agregar Clase (local)
                  </button>
                </div>

                {/* Lista “local” deshabilitada (la real la maneja UdemyFilmVideos) */}
                <ul className="space-y-1 mt-2 opacity-60 pointer-events-none">
                  {section.lectures?.length === 0 && <p className="text-gray-500 text-sm">No hay clases locales en esta sección.</p>}
                  {section.lectures?.map(lecture => (
                    <li key={lecture.id} className="flex items-center p-2">
                      <span className="flex-1">{lecture.title} ({Math.floor(lecture.duration / 60)} min)</span>
                      <button
                        onClick={() => deleteLecture(section.id, lecture.id)}
                        className="text-gray-500"
                        title="Eliminar clase local"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UdemyCourseStructure;
